/*
【仕様変更】施設予約抽選期間の開始をラベルで1固定に変更
　※再度戻す場合に備えて仕様変更前の内容をコメントアウト
 */
import React, {Component, ReactElement} from 'react'

import NatCondominiumRepo from './repository/NatCondominiumRepo'
import {CondominiumData, NatGetCondominium} from './repository/NatGetCondominium'
import {NatPutCondominium, NatPutCondominiumParams} from './repository/NatPutCondominium'
import NatCondominiumGroupRepo from '../condominiumGroup/repository/NatCondominiumGroupRepo'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../app/TypeHelpers'
import NatUseFunctionMasterRepo from '../useFunctionMaster/repository/NatUseFunctionMasterRepo'
import {Messages} from '../../../Messages'
import {MESSAGE_TYPE} from '../../../CodeMaster'
import {NatAdminCondominiumForm} from './NatAdminCondominiumForm'
import {CondominiumGroupData, NatGetCondominiumGroup} from '../condominiumGroup/repository/NatGetCondominiumGroup'
import {NatGetUseFunctionMaster, UseFunctionMaster} from '../useFunctionMaster/repository/NatGetUseFunctionMaster'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    condominiumRepo: NatCondominiumRepo
    condominiumGroupRepo: NatCondominiumGroupRepo
    useFunctionMasterRepo: NatUseFunctionMasterRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    isLoadCompleted: boolean
    condominiumGroup: CondominiumGroupData
    useFunctionMaster: UseFunctionMaster[]
    condominium?: CondominiumData
}

class NatAdminCondominiumEditPage extends Component<Props & AdminPagePropsSet, States> {
    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        this.state = {
            isLoadCompleted: false,
            condominiumGroup: {
                level1CondominiumGroupId: '',
                level1CondominiumGroupName: '',
                level1CondominiumGroupVersion: 0,
                level2CondominiumGroupInfo: [],
            },
            useFunctionMaster: [],
            condominium: undefined,
        }

        this.registerCondominiumButton = this.registerCondominiumButton.bind(this)
        this.handleRegisterCancelClick = this.handleRegisterCancelClick.bind(this)
        this.condominiumDetailLinkClick = this.condominiumDetailLinkClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {

            // 物件グループマスタ一覧取得
            const condominiumGroupResponse: Nullable<NatGetCondominiumGroup> = await this.props.asyncCallFunction(
                this.props.condominiumGroupRepo.getCondominiumGroup.bind(this.props.condominiumGroupRepo),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
            if (condominiumGroupResponse && condominiumGroupResponse.details) {
                this.setState({
                    condominiumGroup: condominiumGroupResponse.details,
                })
            }

            // 利用機能マスタ一覧取得
            const useFunctionMasterResponse: Nullable<NatGetUseFunctionMaster> = await this.props.asyncCallFunction(
                this.props.useFunctionMasterRepo.getUseFunctionMaster.bind(this.props.useFunctionMasterRepo),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
            const useFunctionMaster: UseFunctionMaster[] =
                (useFunctionMasterResponse && useFunctionMasterResponse.details) ?
                    useFunctionMasterResponse.details : []

            // 物件取得
            const condominiumResponse: Nullable<NatGetCondominium> = await this.props.asyncCallFunction(
                this.props.condominiumRepo.getCondominium.bind(this.props.condominiumRepo, this.props.condominiumCd),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
            if (condominiumResponse && condominiumResponse.details) {
                this.setState({
                    useFunctionMaster: useFunctionMaster,
                    condominium: condominiumResponse.details,
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    // 登録ボタン押下
    async registerCondominiumButton(condominium: NatPutCondominiumParams): Promise<void> {

        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0010.errorLevel,
                message: Messages.IOPCC0010.toString(condominium.condominiumName),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: () => this.handleRegisterOkClick(condominium),
                    },
                ],
            },
        )
    }

    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleRegisterOkClick(putCondominiumParams: NatPutCondominiumParams): Promise<void> {
        this.props.eventEmitter.closeDialog()
        this.props.eventEmitter.clearBroadcastedErrors()
        await this.props.eventEmitter.showLoading()

        try {
            const putCondominiumResponse: Nullable<NatPutCondominium> = await this.props.asyncCallFunction(
                this.props.condominiumRepo.putCondominium.bind(this.props.condominiumRepo, putCondominiumParams),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {defaultErrorTitle: Messages.EOPCA0002.toString('物件')},
            )
            if (putCondominiumResponse && putCondominiumResponse.requestError === null) {
                this.props.navigate(
                    `/admin/condominium/${putCondominiumParams.condominiumCd}`,
                    {
                        state: {
                            initialMessage: {
                                title: null,
                                messageLevel: MESSAGE_TYPE.INFORMATION,
                                detail: Messages.IOPCC0005.toString('物件', putCondominiumParams.condominiumName),
                            },
                        },
                    },
                )
            } else {
                return
            }

        } finally {
            this.props.eventEmitter.closeLoading()
        }

    }

    condominiumDetailLinkClick(): void {
        this.props.navigate(
            `/admin/condominium/${this.props.condominiumCd}`,
        )
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }
        return <>
            <NatAdminCondominiumForm
                mode={'edit'}
                condominium={this.state.condominium}
                condominiumGroup={this.state.condominiumGroup}
                useFunctionMaster={this.state.useFunctionMaster}
                condominiumRepo={this.props.condominiumRepo}
                eventEmitter={this.props.eventEmitter}
                onEditCondominium={this.registerCondominiumButton}
                backButtonText={'物件詳細へ戻る'}
                onBack={this.condominiumDetailLinkClick}
            />
        </>
    }
}

export default createAdminPage(NatAdminCondominiumEditPage)
